import React, { useEffect, useState, useRef, memo, createElement } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Zap, Target, CheckCircle2, Star, Users, Gift, Sparkles, Flower } from 'lucide-react';
import { useMotionValue, useTransform, animate, useInView, AnimatePresence } from 'framer-motion';
import { UrgencyBanner } from './components/UrgencyBanner';
import { BonusCard } from './components/BonusCard';
import { Reviews } from './components/Reviews';
import { FAQ } from './components/FAQ';
import { WhatYouReceive } from './components/WhatYouReceive';
import { MagicalBackground } from './components/MagicalBackground';
import { InfraestructuraSistemica } from './components/InfraestructuraSistemica';
import { DiferencialHibrido } from './components/DiferencialHibrido';
import { PremiumCTA } from './components/PremiumCTA';
import { PainPoints } from './components/PainPoints';
function AnimatedCounter({
  from,
  to
}: {
  from: number;
  to: number;
}) {
  const count = useMotionValue(from);
  const rounded = useTransform(count, latest => {
    return Math.round(latest).toLocaleString('es-AR');
  });
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true
  });
  useEffect(() => {
    if (isInView) {
      const controls = animate(count, to, {
        duration: 1.5,
        ease: 'easeOut'
      });
      return controls.stop;
    }
  }, [to, isInView]);
  return <motion.span ref={ref} data-id="element-0">{rounded}</motion.span>;
}
const testimonials = [{
  name: 'Beatriz',
  loc: 'Buenos Aires',
  badge: 'Terapeuta Holística',
  text: 'A mis 55 años, buscaba algo que realmente le diera un marco profesional a mis lecturas. El sistema de interpretación terapéutica es oro puro; me dio la estructura que me faltaba para mis sesiones individuales.'
}, {
  name: 'Silvia',
  loc: 'Córdoba Capital',
  badge: 'Lectora Profesional',
  text: 'El manual fundamental cambió totalmente la profundidad de mis lecturas. Las preguntas de exploración generan un clima de confianza y descubrimiento inmediato que mis consultantes agradecen muchísimo.'
}, {
  name: 'Claudia',
  loc: 'Rosario',
  badge: 'Facilitadora de Tarot',
  text: 'Los métodos de tiradas terapéuticas son los más potentes que he usado en mis 15 años de carrera. Mis consultantes están fascinados con la claridad y precisión de los procesos emocionales que logramos.'
}, {
  name: 'Marcela',
  loc: 'Mendoza',
  badge: 'Psicóloga Holística',
  text: 'El diario de interpretación es la herramienta perfecta para que el proceso terapéutico siga trabajando entre sesiones. A mis 50 años, valoro tener materiales tan bien organizados y profesionales.'
}, {
  name: 'Adriana',
  loc: 'Salta',
  badge: 'Terapeuta Energética',
  text: 'El protocolo de protección energética me salvó. Antes terminaba agotada después de cada lectura, ahora sé cómo sostener mi energía y mi lugar como profesional sin cargar con lo ajeno.'
}];
export function App() {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [spots, setSpots] = useState(18);
  const [joinedCount, setJoinedCount] = useState(3265);
  const [viewers, setViewers] = useState(48);
  const [showSaleAnimations, setShowSaleAnimations] = useState(false);
  useEffect(() => {
    const viewerInterval = setInterval(() => {
      setViewers(prev => prev + (Math.random() > 0.5 ? 1 : -1));
    }, 3000);
    return () => clearInterval(viewerInterval);
  }, []);
  useEffect(() => {
    // Meta Pixel Integration
    const pixelId = '1051253568074079';
    // @ts-ignore
    if (!window.fbq) {
      // @ts-ignore
      !function (f, b, e, v, n, t, s) {
        if (f.fbq) return;
        n = f.fbq = function () {
          n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) f._fbq = n;
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        t = b.createElement(e);
        t.async = !0;
        t.src = v;
        b.head.appendChild(t);
      }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }
    // @ts-ignore
    window.fbq('init', pixelId);
    // @ts-ignore
    window.fbq('track', 'PageView');
  }, []);
  const handleSale = () => {
    setSpots(prev => prev > 2 ? prev - 1 : prev);
    setJoinedCount(prev => prev + 1);
    setShowSaleAnimations(true);
    setTimeout(() => setShowSaleAnimations(false), 2000);
  };
  const nextTestimonial = () => {
    setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentTestimonial(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const currentDate = new Date().toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const scrollToPurchase = () => {
    document.getElementById('purchase')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen bg-[#fdfbf7] text-[#2d2d2d] font-sans selection:bg-purple-200 selection:text-purple-900" data-id="element-1">
      <MagicalBackground data-id="element-2" />
      <UrgencyBanner spots={spots} onSale={handleSale} viewers={viewers} data-id="element-3" />

      {/* Hero Section */}
      <header className="relative w-full group" data-id="element-4">
        <img src="https://cdn.magicpatterns.com/uploads/6aMwHELnkcMoLSp6fcZ2gu/ChatGPT_Image_10_may_2026,_12_38_36.jpg" alt="Aprendé a Interpretar el Tarot - Tarot Terapéutico Profesional" className="w-full h-auto mb-[0px]" data-id="element-5" />

        {/* Premium Holographic CTA Button — overlaid on top of painted button area */}
        <div className="absolute bottom-[0.2%] left-1/2 -translate-x-1/2 w-[88%] z-30" data-id="element-6">
          <motion.button onClick={scrollToPurchase} whileHover={{
          scale: 1.01
        }} whileTap={{
          scale: 0.98
        }} aria-label="Quiero transformar mi don" className="group/btn relative w-full cursor-pointer" style={{
          aspectRatio: '5.2 / 1'
        }} data-id="element-7">
            {/* Holographic iridescent border (animated gradient) */}
            <span className="absolute inset-0 rounded-[14px] p-[1.5px] opacity-100 group-hover/btn:opacity-100 transition-opacity duration-500" style={{
            background: 'linear-gradient(120deg, #b794f4 0%, #f0abfc 18%, #fde68a 36%, #5eead4 54%, #93c5fd 72%, #c4b5fd 90%, #f0abfc 100%)',
            backgroundSize: '300% 300%',
            animation: 'mp-holo-shift 8s linear infinite'
          }} data-id="element-8">
              {/* Inner dark fill */}
              <span className="block w-full h-full rounded-[12px] bg-black" data-id="element-9" />
            </span>

            {/* Outer glow on hover */}
            <span className="pointer-events-none absolute -inset-2 rounded-[20px] opacity-0 group-hover/btn:opacity-70 blur-xl transition-opacity duration-500" style={{
            background: 'linear-gradient(120deg, rgba(183,148,244,0.5), rgba(94,234,212,0.5), rgba(147,197,253,0.5))'
          }} data-id="element-10" />

            {/* Label + sparkles */}
            <span className="absolute inset-0 flex items-center justify-center px-4 gap-[20px]" data-id="element-11">
              <span aria-hidden="true" className="text-[#fde68a] text-base md:text-2xl drop-shadow-[0_0_6px_rgba(253,224,138,0.6)]" data-id="element-12">
                ✦
              </span>
              <span className="font-black uppercase tracking-[0.18em] md:text-xl text-[10.5px]" style={{
              background: 'linear-gradient(90deg, #c4b5fd 0%, #fde68a 50%, #93c5fd 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }} data-id="element-13">
                QUIERO TRANSFORMAR MI DON
              </span>
              <span aria-hidden="true" className="text-[#fde68a] text-base md:text-2xl drop-shadow-[0_0_6px_rgba(253,224,138,0.6)]" data-id="element-14">
                ✦
              </span>
            </span>

            {/* Shine sweep — runs every ~4.5s */}
            <span className="absolute inset-0 overflow-hidden rounded-[14px] pointer-events-none" data-id="element-15">
              <span className="absolute top-0 -left-1/3 h-full w-1/3 skew-x-[-20deg]" style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.18) 50%, transparent 100%)',
              animation: 'mp-shine-sweep 4.5s ease-in-out infinite'
            }} data-id="element-16" />
            </span>
          </motion.button>
        </div>
      </header>

      <PremiumCTA data-id="element-17" />

      {/* Sales Copy */}
      <section className="bg-transparent" data-id="element-18">
        <div className="w-full" data-id="element-19">
          <PainPoints data-id="element-20" />

          <div className="max-w-4xl mx-auto px-4 pl-[0px] pr-[0px]" data-id="element-21">
            <DiferencialHibrido data-id="element-22" />
            <InfraestructuraSistemica data-id="element-23" />
          </div>
        </div>
      </section>

      <WhatYouReceive data-id="element-24" />

      {/* Bonuses */}
      <section className="py-24 bg-transparent relative overflow-hidden pt-[15px] pb-[15px]" data-id="element-25">
        {/* Decorative magical background — stars, moons, faint tarot cards */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-26">
          {/* Soft radial wash */}
          <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 20% 10%, rgba(253,224,138,0.08) 0%, transparent 55%), radial-gradient(ellipse at 85% 90%, rgba(124,58,237,0.07) 0%, transparent 55%)'
        }} data-id="element-27" />

          {/* Faint tarot card silhouettes */}
          <svg className="absolute -left-6 top-12 w-32 h-44 opacity-[0.06] -rotate-12" viewBox="0 0 80 110" fill="none" stroke="#4c1d95" strokeWidth="1.2" data-id="element-28">
            <rect x="3" y="3" width="74" height="104" rx="6" data-id="element-29" />
            <rect x="9" y="9" width="62" height="92" rx="3" data-id="element-30" />
            <circle cx="40" cy="55" r="14" data-id="element-31" />
            <path d="M40 41 L43 51 L53 51 L45 57 L48 67 L40 61 L32 67 L35 57 L27 51 L37 51 Z" data-id="element-32" />
          </svg>
          <svg className="absolute right-2 top-1/3 w-28 h-40 opacity-[0.05] rotate-[14deg]" viewBox="0 0 80 110" fill="none" stroke="#4c1d95" strokeWidth="1.2" data-id="element-33">
            <rect x="3" y="3" width="74" height="104" rx="6" data-id="element-34" />
            <rect x="9" y="9" width="62" height="92" rx="3" data-id="element-35" />
            <path d="M40 25 Q55 55 40 90 Q25 55 40 25 Z" data-id="element-36" />
          </svg>
          <svg className="absolute left-1/4 bottom-10 w-24 h-36 opacity-[0.05] rotate-[6deg]" viewBox="0 0 80 110" fill="none" stroke="#4c1d95" strokeWidth="1.2" data-id="element-37">
            <rect x="3" y="3" width="74" height="104" rx="6" data-id="element-38" />
            <rect x="9" y="9" width="62" height="92" rx="3" data-id="element-39" />
            <circle cx="40" cy="55" r="20" data-id="element-40" />
            <circle cx="40" cy="55" r="12" data-id="element-41" />
          </svg>

          {/* Minimalist moons */}
          <svg className="absolute right-[8%] top-[8%] w-10 h-10 opacity-30" viewBox="0 0 40 40" fill="none" data-id="element-42">
            <path d="M28 6 A16 16 0 1 0 28 34 A12 12 0 1 1 28 6 Z" fill="#d4af37" opacity="0.45" data-id="element-43" />
          </svg>
          <svg className="absolute left-[10%] bottom-[20%] w-7 h-7 opacity-25" viewBox="0 0 40 40" fill="none" data-id="element-44">
            <path d="M28 6 A16 16 0 1 0 28 34 A12 12 0 1 1 28 6 Z" fill="#7c3aed" opacity="0.4" data-id="element-45" />
          </svg>

          {/* Gold stars (twinkling) */}
          {[{
          top: '6%',
          left: '12%',
          size: 10,
          delay: 0
        }, {
          top: '18%',
          left: '48%',
          size: 8,
          delay: 0.6
        }, {
          top: '12%',
          right: '22%',
          size: 12,
          delay: 1.2
        }, {
          top: '55%',
          left: '6%',
          size: 9,
          delay: 0.3
        }, {
          top: '70%',
          right: '12%',
          size: 11,
          delay: 0.9
        }, {
          bottom: '8%',
          left: '38%',
          size: 8,
          delay: 1.5
        }, {
          bottom: '22%',
          right: '38%',
          size: 10,
          delay: 0.4
        }, {
          top: '40%',
          right: '6%',
          size: 7,
          delay: 1.8
        }].map((s, i) => <motion.div key={i} animate={{
          opacity: [0.3, 0.9, 0.3],
          scale: [0.9, 1.15, 0.9]
        }} transition={{
          duration: 3 + i % 3,
          repeat: Infinity,
          delay: s.delay,
          ease: 'easeInOut'
        }} className="absolute" style={{
          top: s.top,
          left: s.left,
          right: s.right,
          bottom: s.bottom
        }} data-id="element-46">
              <svg width={s.size} height={s.size} viewBox="0 0 24 24" fill="#d4af37" data-id="element-47">
                <path d="M12 2 L14 9 L21 10 L16 15 L17 22 L12 18 L7 22 L8 15 L3 10 L10 9 Z" data-id="element-48" />
              </svg>
            </motion.div>)}

          {/* Soft particles */}
          {[{
          top: '25%',
          left: '20%'
        }, {
          top: '60%',
          left: '70%'
        }, {
          top: '35%',
          left: '80%'
        }, {
          bottom: '30%',
          left: '15%'
        }, {
          bottom: '15%',
          right: '25%'
        }].map((p, i) => <motion.div key={`p-${i}`} animate={{
          y: [0, -10, 0],
          opacity: [0.2, 0.5, 0.2]
        }} transition={{
          duration: 4 + i,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: i * 0.4
        }} className="absolute w-1.5 h-1.5 rounded-full" style={{
          ...p,
          background: 'radial-gradient(circle, rgba(253,224,138,0.8) 0%, transparent 70%)'
        }} data-id="element-49" />)}
        </div>

        <div className="max-container px-4 relative z-10" data-id="element-50">
          <div className="text-center mb-16" data-id="element-51">
            <motion.div initial={{
            scale: 0.9,
            opacity: 0
          }} whileInView={{
            scale: 1,
            opacity: 1
          }} viewport={{
            once: true
          }} className="inline-flex items-center justify-center mb-6" data-id="element-52">
              <div className="relative" data-id="element-53">
                <motion.div animate={{
                rotate: [0, -10, 10, -10, 0]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} className="bg-purple-600 text-white p-4 rounded-2xl shadow-xl relative z-10" data-id="element-54">
                  <Gift size={40} data-id="element-55" />
                </motion.div>
                <motion.div animate={{
                scale: [1, 1.2, 1],
                opacity: [0.5, 0.8, 0.5]
              }} transition={{
                duration: 2,
                repeat: Infinity
              }} className="absolute -inset-4 bg-purple-200/30 rounded-full blur-xl" data-id="element-56" />
              </div>
            </motion.div>

            <h2 className="md:text-7xl font-bold text-purple-950 uppercase italic mb-4 text-[32px] font-magical tracking-tight" data-id="element-57">
              ¡Regalos <span className="text-purple-600" data-id="element-58">Exclusivos!</span>
            </h2>
            <div className="flex items-center justify-center gap-2" data-id="element-59">
              <Sparkles className="text-amber-500 animate-pulse" size={20} data-id="element-60" />
              <p className="text-purple-600 font-black tracking-[0.2em] uppercase text-sm" data-id="element-61">
                100% GRATIS SOLO POR HOY
              </p>
              <Sparkles className="text-amber-500 animate-pulse" size={20} data-id="element-62" />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-63">
            <BonusCard number="BONO 01" title="Checklist de Preparación" image="https://cdn.magicpatterns.com/uploads/qnHBLUKYy7DnKx8xq2SzXu/Book_cover_for_Bono_1_202605101358.webp" description="Guía rápida para preparar correctamente el espacio energético y emocional antes de cada lectura." originalPrice="$12.500 ARS" value="HOY GRATIS" data-id="element-64" />
            <BonusCard number="BONO 02" title="Manual de Interpretación de la Carta Astral" image="https://cdn.magicpatterns.com/uploads/anfLkeSD3oYmS5cK9dr2eL/Book_cover_design_interpretation_202605101358.webp" description="Introducción práctica para integrar elementos astrológicos dentro del acompañamiento terapéutico." originalPrice="$14.900 ARS" value="HOY GRATIS" data-id="element-65" />
            <BonusCard number="BONO 03" title="Diario de Armonización y Frecuencia Interior" image="https://cdn.magicpatterns.com/uploads/w8DtoGTLWGdTdacXYbvKbY/Book_cover_for_Bono_3_202605101359.webp" description="Ejercicios guiados para fortalecer conexión interior, presencia y equilibrio energético." originalPrice="$11.200 ARS" value="HOY GRATIS" data-id="element-66" />
            <BonusCard number="BONO 04" title="Método de Protección y Limpieza Energética" image="https://cdn.magicpatterns.com/uploads/7ffKoPtdj1P1Jd9zVxUDJu/Book_cover_for_Bono_4_202605101401.webp" description="Técnicas prácticas para limpiar, proteger y sostener la energía antes y después de las sesiones." originalPrice="$13.500 ARS" value="HOY GRATIS" data-id="element-67" />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 relative overflow-hidden bg-transparent" data-id="element-68">
        <div className="max-container px-4 relative z-10" data-id="element-69">
          <div className="max-w-2xl mx-auto relative" data-id="element-70">
            <div className="overflow-hidden" data-id="element-71">
              <motion.div key={currentTestimonial} drag="x" dragConstraints={{
              left: 0,
              right: 0
            }} onDragEnd={(_, info) => {
              if (info.offset.x > 100) {
                prevTestimonial();
              } else if (info.offset.x < -100) {
                nextTestimonial();
              }
            }} initial={{
              opacity: 0,
              x: 100
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -100
            }} transition={{
              duration: 0.5,
              ease: 'easeOut'
            }} className="bg-white/80 p-6 md:p-8 rounded-3xl border border-purple-100 relative backdrop-blur-sm cursor-grab active:cursor-grabbing shadow-lg premium-shadow" data-id="element-72">
                <div className="flex flex-col items-center text-center" data-id="element-73">
                  <div className="flex-1" data-id="element-74">
                    <div className="flex justify-center text-purple-500 mb-3 gap-1" data-id="element-75">
                      {[...Array(5)].map((_, idx) => <Star key={idx} size={14} fill="currentColor" strokeWidth={1} data-id="element-76" />)}
                    </div>

                    <p className="text-zinc-800 text-base md:text-lg leading-relaxed font-light italic mb-4" data-id="element-77">
                      "{testimonials[currentTestimonial].text}"
                    </p>

                    <div className="pt-3 border-t border-purple-50" data-id="element-78">
                      <h4 className="text-purple-950 font-bold text-base" data-id="element-79">
                        {testimonials[currentTestimonial].name}
                      </h4>
                      <p className="text-purple-600 font-bold uppercase tracking-widest text-[10px]" data-id="element-80">
                        {testimonials[currentTestimonial].loc} —{' '}
                        {testimonials[currentTestimonial].badge}
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Navigation Arrows */}
            <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-10 bg-white p-2 rounded-full text-purple-600 hover:bg-purple-50 transition-colors z-20 shadow-md border border-purple-100" data-id="element-81">
              <ArrowRight className="rotate-180" size={18} data-id="element-82" />
            </button>
            <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-10 bg-white p-2 rounded-full text-purple-600 hover:bg-purple-50 transition-colors z-20 shadow-md border border-purple-100" data-id="element-83">
              <ArrowRight size={18} data-id="element-84" />
            </button>
          </div>
        </div>
      </section>

      {/* Purchase Section */}
      <section id="purchase" className="py-24 bg-[#fdfbf7] relative overflow-hidden pt-[30px] pb-[30px]" data-id="element-85">
        <div className="max-w-4xl mx-auto px-4 relative z-10" data-id="element-86">
          <div className="bg-purple-600 text-white p-4 text-center font-black uppercase tracking-widest mb-8 animate-pulse rounded-xl shadow-lg" data-id="element-87">
            OFERTA VÁLIDA SOLO POR HOY: {currentDate}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12" data-id="element-88">
            {['Descargue todos los archivos, guárdelos en una carpeta segura y téngalos para siempre.', 'Pago único y descarga inmediata.', 'Materiales listos para impresión.', 'Herramientas desarrolladas por terapeutas especialistas.'].map((text, i) => <div key={i} className="flex items-center gap-3 bg-white p-4 rounded-2xl border border-purple-100 shadow-sm" data-id="element-89">
                <CheckCircle2 className="text-purple-600 shrink-0" size={20} data-id="element-90" />
                <p className="text-zinc-800 text-sm font-bold leading-tight" data-id="element-91">
                  {text}
                </p>
              </div>)}
          </div>

          <div className="max-w-3xl mx-auto" data-id="element-92">
            <div className="bg-white border border-purple-100 rounded-[2.5rem] shadow-2xl overflow-hidden premium-shadow" data-id="element-93">
              <div className="p-6 md:p-12 flex flex-col items-center text-center pt-[24px] pb-[24px] pr-[24px]" data-id="element-94">
                <div className="mb-8 w-full" data-id="element-95">
                  <img src="https://cdn.magicpatterns.com/uploads/cfQrdUBoTmXeXJSpkVXJys/Libro_Tarot_Terapeutico_Profesio%E2%80%A6_202605101407.jpg" alt="Sistema Profesional de Tarot Terapéutico" className="w-full h-auto rounded-3xl shadow-xl border border-purple-50" data-id="element-96" />
                </div>

                {/* Social Proof */}
                <div className="mb-[24px] space-y-[0px]" data-id="element-97">
                  <p className="text-purple-600 font-black text-sm tracking-widest uppercase" data-id="element-98">
                    + <AnimatedCounter from={0} to={joinedCount} data-id="element-99" /> ALUMNOS
                  </p>
                  <div className="flex justify-center gap-1 text-amber-400" data-id="element-100">
                    {[...Array(5)].map((_, i) => <Star key={i} size={24} fill="currentColor" data-id="element-101" />)}
                  </div>
                  <p className="text-purple-400 font-bold text-[12px]" data-id="element-102">
                    (1.728 reseñas)
                  </p>
                </div>

                {/* Title & Urgency */}
                <div className="mb-8" data-id="element-103">
                  <h2 className="md:text-4xl font-bold text-purple-950 text-[20px] font-magical mb-[0px]" data-id="element-104">
                    Sistema Profesional de Tarot Terapéutico
                    <br data-id="element-105" />
                    <span className="text-purple-600" data-id="element-106">+ 4 Bonos de regalo</span>
                  </h2>
                </div>

                {/* Pricing Section */}
                <div className="mb-8 space-y-2" data-id="element-107">
                  <p className="md:text-8xl font-black text-[#319E0D] tracking-tighter text-[45px]" data-id="element-108">
                    $19.990 ARS
                  </p>
                </div>

                {/* Discount Badge */}
                <div className="mb-8" data-id="element-109">
                  <div className="inline-flex items-center gap-2 bg-purple-600 text-white px-6 py-2 rounded-full font-black uppercase tracking-wider shadow-lg text-[12px] pb-[5px] pt-[5px]" data-id="element-110">
                    50% OFF HOY
                  </div>
                </div>

                {/* CTA Button */}
                <div className="w-full max-w-md mb-8" data-id="element-111">
                  <a href="https://codex-labs.shop/cart/43501032210491:1" target="_blank" rel="noopener noreferrer" className="w-full bg-[#84cc16] hover:bg-[#65a30d] text-white py-6 rounded-2xl font-black text-3xl flex items-center justify-center gap-3 transition-all shadow-[0_15px_30px_rgba(132,204,22,0.3)] hover:scale-[1.02] active:scale-95 uppercase tracking-tighter pt-[24px]" data-id="element-112">
                    ACCEDER AHORA
                  </a>
                  <div className="mt-4 flex items-center justify-center gap-2 animate-pulse" data-id="element-113">
                    <span className="text-2xl" data-id="element-114">✨</span>
                    <p className="text-purple-600 font-black text-[16px]" data-id="element-115">
                      ¡Últimos {spots} cupos en oferta!
                    </p>
                  </div>
                </div>

                <img src="https://cdn.magicpatterns.com/uploads/eU118ieSDgWuz64T6oQtAx/capture_260410_163646.png" alt="Pago Seguro Garantizado" className="w-full h-auto object-contain mb-[0px] rounded-2xl opacity-80" data-id="element-116" />

                {/* Features List (Integrated) */}

                {/* Trust Section */}
                <div className="border-t border-purple-50 w-full pt-[0px] mt-[0px] space-y-[0px]" data-id="element-117">
                  {/* Primary Trust Signals - Large and Stacked */}
                  <div className="text-center" data-id="element-118">
                    <img src="https://cdn.magicpatterns.com/uploads/skccFFLzCHBogMzzjkfEmF/7ef4071d-c511-4325-9d9d-f2158f7b8632.jpg" alt="MercadoLíder Platinum" className="h-40 md:h-56 mx-auto object-contain shadow-lg rounded-2xl hover:scale-105 transition-transform duration-300" data-id="element-119" />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 items-center bg-purple-50/50 p-6 rounded-3xl border border-purple-100 mt-[15px] pt-[0px] pb-[0px] pl-[0px] pr-[0px] gap-[0px]" data-id="element-120">
                    <div className="text-center space-y-2" data-id="element-121"></div>

                    <div className="text-center space-y-2 border-t md:border-t-0 md:border-l border-purple-100 pt-4 md:pt-0 md:pl-6" data-id="element-122">
                      <p className="text-purple-900 font-black text-[10px] uppercase tracking-widest mb-2" data-id="element-123">
                        Tu Compra está Protegida
                      </p>
                      <div className="flex flex-row items-center justify-center gap-6" data-id="element-124">
                        <div className="shine-container rounded-lg" data-id="element-125">
                          <img src="https://cdn.magicpatterns.com/uploads/7eCtKk7WA3cLyGaeie1cdM/png-clipart-safe-purchase-payment-security-shop-shopping-online-store-e-commerce-sign-green-texta.png" alt="Compra Segura" className="h-20 md:h-28 object-contain" data-id="element-126" />
                        </div>
                        <div className="shine-container rounded-lg" data-id="element-127">
                          <img src="https://cdn.magicpatterns.com/uploads/fpbSfdYZaTGuGJTHBKUizP/Selo_de_Garantia_de_7_Dias_PNG_Transparente_Sem_Fundo.png" alt="Garantía de 7 Días" className="h-24 md:h-32 object-contain" data-id="element-128" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-center gap-2" data-id="element-129">
                    <p className="text-zinc-500 text-[10px] font-bold flex items-center gap-2 uppercase tracking-widest" data-id="element-130">
                      <ShieldCheck size={14} className="text-green-600" data-id="element-131" /> Pago
                      100% Seguro y Encriptado
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Reviews data-id="element-132" />

      <FAQ data-id="element-133" />

      <footer className="py-12 border-t border-purple-100 text-center text-zinc-400 text-sm" data-id="element-134">
        <p data-id="element-135">
          © 2025 Sistema de Tarot Terapéutico. Todos los derechos reservados.
        </p>
      </footer>
    </div>;
}